import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input, OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewChild
} from '@angular/core';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {
  AbstractControl,
  ControlContainer, ControlValueAccessor, FormArray,
  FormControl, FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {map, startWith, tap} from 'rxjs/operators';
import {
  MatAutocomplete,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';

export interface Tag {
  label: string;
  value: string;
}

@Component({
  selector: 'app-multi-select-dropdown',
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: MultiSelectDropdownComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: MultiSelectDropdownComponent
    }
  ]
})
export class MultiSelectDropdownComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  @Input() public formControlName: string;
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public tagOptions: Tag[] = [];

  @ViewChild('chipInput') chipInput: ElementRef;
  @ViewChild('trigger') autoCompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('auto') matAutoComplete: MatAutocomplete;
  @ViewChild('componentContainer') componentContainer: ElementRef<HTMLDivElement>;
  @ViewChild('chipListContainer') chipListContainer: ElementRef;
  @ViewChild('searchContainer') searchContainer: ElementRef;
  @ViewChild('arrowContainer') arrowContainer: ElementRef;

  public readonly separatorKeysCodes = [ENTER, COMMA] as const;
  public addOnBlur = true;
  public selectedTags = [];
  public myControl = new FormControl('');
  public filteredOptions: Observable<Tag[]>;

  public touched = false;
  public disabled = false;
  public isControlValid = true;
  private statusChangesSubscription: Subscription;
  public panelWidth: number;
  private componentClasses = ['clickatell-multiselect-control-input', 'mat-chip-input', 'mat-input-element', 'mat-autocomplete-trigger', 'mat-chip-list-wrapper', 'mat-checkbox-label', 'mat-checkbox-layout', 'mat-checkbox-inner-container'];
  private controlResizeObserver: ResizeObserver;

  constructor(@Optional() private controlContainer: ControlContainer, private renderer: Renderer2) { }

  ngOnInit(): void {
    this.filteredOptions = this.myControl.valueChanges.pipe(
        startWith(''),
        map(value => {
          if (typeof value === 'string') {
            return value;
          } else {
            return '';
          }
        }),
        map(value => this._filter(value || '')),
    );
  }

  ngAfterViewInit(): void {
    if (this.selectedTags.length >= 1) {
      this.showChipsContainer();
      this.hideSearchContainer();
    } else {
      this.hideChipsContainer();
      this.showSearchContainer();
    }
    this.listenForControlResize();
    this.listenForControlStatusChanges();
    this.validate(this.control);
  }

  listenForControlResize() {
    this.panelWidth = this.componentContainer.nativeElement.offsetWidth;
    this.controlResizeObserver = new ResizeObserver(entries => {
      this.panelWidth = entries[0].contentRect.width;
    });
    this.controlResizeObserver.observe(this.componentContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.statusChangesSubscription?.unsubscribe();
  }

  private listenForControlStatusChanges() {
    this.hasErrorsCheck();
    this.statusChangesSubscription = this.control.statusChanges.pipe(
        tap(isValid => {
          if (isValid === 'VALID') {
            this.isControlValid = true;
          }
          if (isValid === 'INVALID') {
            this.isControlValid = false;
          }
        }),
    ).subscribe();
  }

  private hasErrorsCheck() {
    if (this.control.errors) {
      this.isControlValid = false;
    } else {
      this.isControlValid = true;
    }
  }

  get form(): FormGroup {
    return this.controlContainer.control as FormGroup;
  }

  get control(): FormArray {
    return this.form.get(this.formControlName) as FormArray;
  }

  onChange = (selectedTags) => { };
  onTouched = () => { };

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  writeValue(selectedTags: string[]) {
    this.selectedTags = selectedTags;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  private _filter(value: string): Tag[] {
    if (!this.tagOptions) {
      return [];
    }
    const filterValue = value.toLowerCase();
    return this.tagOptions.filter(option => option['label'].toLowerCase().includes(filterValue));
  }

  public toggleSearching() {
    if (this.chipListContainer.nativeElement.classList.contains('show')) {
      this.hideChipsShowInput();
    } else {
      this.showChipsHideInput();
    }
  }

  public showChipsHideInput() {
    if (this.selectedTags.length > 0) {
      this.autoCompleteTrigger.closePanel();
      this.showPanelCloseArrow();
      this.showChipsContainer();
      this.hideSearchContainer();
      this.renderer.removeClass(this.componentContainer.nativeElement, 'active');
      this.chipInput.nativeElement.blur();
      this.myControl.reset();
    } else {
      if (this.arrowContainer.nativeElement.classList.contains('panelClosedArrow')) {
        this.showPanelOpenArrow();
      } else if (this.arrowContainer.nativeElement.classList.contains('panelOpenArrow')) {
        this.showPanelCloseArrow();
        this.autoCompleteTrigger.closePanel();
      }
    }
  }

  public hideChipsShowInput() {
    this.showPanelOpenArrow();
    this.hideChipsContainer();
    this.showSearchContainer();
    this.autoCompleteTrigger.openPanel();
    this.renderer.addClass(this.componentContainer.nativeElement, 'active');
    this.chipInput.nativeElement.focus();
  }

  showPanelOpenArrow() {
    this.renderer.addClass(this.arrowContainer.nativeElement, 'panelOpenArrow');
    this.renderer.removeClass(this.arrowContainer.nativeElement, 'panelClosedArrow');
  }

  showPanelCloseArrow() {
    this.renderer.addClass(this.arrowContainer.nativeElement, 'panelClosedArrow');
    this.renderer.removeClass(this.arrowContainer.nativeElement, 'panelOpenArrow');
  }

  showChipsContainer() {
    this.renderer.addClass(this.chipListContainer.nativeElement, 'show');
    this.renderer.removeClass(this.chipListContainer.nativeElement, 'hide');
  }

  hideChipsContainer() {
    this.renderer.addClass(this.chipListContainer.nativeElement, 'hide');
    this.renderer.removeClass(this.chipListContainer.nativeElement, 'show');
  }

  showSearchContainer() {
    this.renderer.addClass(this.searchContainer.nativeElement, 'show');
    this.renderer.removeClass(this.searchContainer.nativeElement, 'hide');
  }

  hideSearchContainer() {
    this.renderer.addClass(this.searchContainer.nativeElement, 'hide');
    this.renderer.removeClass(this.searchContainer.nativeElement, 'show');
  }

  @HostListener('document:click', ['$event']) click(event) {
    event.target.classList.forEach(c => {
      if (!this.componentClasses.includes(event.target.classList[0])) {
        if (this.autoCompleteTrigger.panelOpen) {
          this.showChipsHideInput();
        }
      }
    });
  }

  onToggleChecked(selectedTag?: Tag) {
    if (!this.selectedTags.some(tag => tag === selectedTag.label)) {
      this.addTag(selectedTag.label);
    } else {
      this.removeTag(selectedTag.label);
    }
  }

  private addTag(selectedTag: string) {
    this.markAsTouched();
    if (!this.disabled) {
      this.selectedTags.push(selectedTag);
      this.onChange(this.selectedTags);
      this.myControl.reset();
      this.chipInput.nativeElement.value = '';
    }
  }

  isTagAdded(tagToCheck: Tag): boolean {
    return this.selectedTags.includes(tagToCheck.label);
  }

  removeTag(tag: string): void {
    this.markAsTouched();
    if (!this.disabled) {
      const index = this.selectedTags.findIndex(savedTag => savedTag === tag);
      if (index >= 0) {
        this.selectedTags.splice(index, 1);
        this.onChange(this.selectedTags);
        if (this.selectedTags.length < 1) {
          this.hideChipsShowInput();
        }
      }
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  scrollBlocker(block) {
    const container = document.querySelector('.form-container') as HTMLElement;
    if (container) {
      block ? container.style['overflow-y'] = 'hidden' : container.style['overflow-y'] = 'auto';
    }
  }

  setFocus() {
    setTimeout(() => {
      this.chipInput.nativeElement.focus();
    }, 100);
  }
}
